import { routeRollsets } from "@/setup/routes/rollshop.routes";
import { alpha, Box, Theme, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { StandReadinessData } from "../models/MillOverview2";

type Props = {
	data: StandReadinessData[];
};

const StandBox = ({
	stand,
	count,
	upcomingSectionId,
}: {
	stand: StandReadinessData;
	count: number;
	upcomingSectionId: number | null;
}) => {
	const haveRollsets = count > 0;
	const statusColor = (theme: Theme) =>
		haveRollsets ? theme.palette.success.main : theme.palette.error.main;

	const content = (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				px: 0.5,
				py: 0.4,
				minWidth: 28,
				borderRadius: 1,
				backgroundColor: (theme) => alpha(statusColor(theme), 0.01),
				border: (theme) => `1px solid ${alpha(statusColor(theme), 0.2)}`,
				transition: "all 0.2s ease",
				"&:hover": {
					backgroundColor: (theme) => alpha(statusColor(theme), 0.15),
					borderColor: (theme) => alpha(statusColor(theme), 0.4),
					transform: "translateY(-1px)",
				},
			}}
		>
			<Box
				sx={{
					width: 20,
					height: 20,
					backgroundColor: statusColor,
					borderRadius: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					mb: 0.25,
				}}
			>
				<Typography
					sx={{
						fontSize: "0.7rem",
						fontWeight: 700,
						color: "#fff",
						lineHeight: 1,
					}}
				>
					{count}
				</Typography>
			</Box>
			<Typography
				sx={{
					fontSize: "0.55rem",
					fontWeight: 600,
					color: (theme) => alpha(theme.palette.text.primary, 0.8),
				}}
			>
				{stand.StandName}
			</Typography>
		</Box>
	);

	if (count === 0 || upcomingSectionId == null) {
		return content;
	}

	return (
		<Tooltip title="Click to view on Rollsets" arrow enterDelay={300}>
			<Link
				to={`/${routeRollsets}/${upcomingSectionId}`}
				target="_blank"
				style={{ textDecoration: "none", color: "inherit" }}
			>
				{content}
			</Link>
		</Tooltip>
	);
};

const ReadinessRow = ({
	label,
	stands,
	getValue,
}: {
	label: string;
	stands: { roughing: StandReadinessData[]; finishing: StandReadinessData[] };
	getValue: (stand: StandReadinessData) => number;
}) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				flexShrink: 0,
			}}
		>
			<Typography
				variant="subtitle2"
				sx={{
					fontWeight: 600,
					fontSize: "0.75rem",
					writingMode: "vertical-rl",
					transform: "rotate(180deg)",
					whiteSpace: "nowrap",
				}}
			>
				{label}
			</Typography>
		</Box>
		<Box
			sx={{
				display: "flex",
				gap: 0.5,
				flexWrap: "wrap",
				justifyContent: "space-between",
				flexGrow: 1,
			}}
		>
			<Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
				{stands.roughing.map((stand) => (
					<StandBox
						key={stand.StandName}
						stand={stand}
						count={getValue(stand)}
						upcomingSectionId={stand.UpcomingSectionId}
					/>
				))}
			</Box>
			<Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
				{stands.finishing.map((stand) => (
					<StandBox
						key={stand.StandName}
						stand={stand}
						count={getValue(stand)}
						upcomingSectionId={stand.UpcomingSectionId}
					/>
				))}
			</Box>
		</Box>
	</Box>
);

export const RollStatusIndicator = ({ data }: Props) => {
	const sortByName = (a: StandReadinessData, b: StandReadinessData) =>
		a.StandName.localeCompare(b.StandName, undefined, { numeric: true });
	const roughing = data.filter((d) => d.StandType === "R").sort(sortByName);
	const finishing = data.filter((d) => d.StandType === "F").sort(sortByName);
	const stands = { roughing, finishing };

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1.5,
			}}
		>
			<ReadinessRow
				label="Rollset"
				stands={stands}
				getValue={(s) => s.ReadyRollsets}
			/>
			<ReadinessRow
				label="Millstand"
				stands={stands}
				getValue={(s) => s.ReadyCarriers}
			/>
		</Box>
	);
};
